import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import Tag from '@components/tag/Tag'
import Checkbox from '@components/checkbox/Checkbox'
import Button from '@components/button/Button'
import Hoverable from '@components/hover/Hoverable'

import useI18n from '@i18n/useI18n'

type TagGroup = 'category' | 'type'

interface Props {
  items: EDLFilter[]
  onSave: (items: EDLFilter[]) => void
  onReset: () => void
  onClose: () => void
  setIsFilterOpen: () => void
  isFilterOpen: boolean
  onHover: (hover: boolean) => void
}

const CATEGORIES: EDLFilter[] = ['TENANT', 'MANIFESTATION']
const TYPES: EDLFilter[] = ['IN', 'OUT']
const TAGS: TagGroup[] = ['category', 'type']

const InlineFilters = ({ items, onSave, onReset, setIsFilterOpen, isFilterOpen, onHover }: Props) => {
  const i18n = useI18n()

  const [local, setLocal] = React.useState<EDLFilter[]>(items)
  const [open, setOpen] = React.useState<TagGroup>()

  React.useEffect(() => {
    setLocal(items)
  }, [items])

  React.useEffect(() => {
    if (!isFilterOpen) {
      setOpen(undefined)
    }
  }, [isFilterOpen])

  const reset = (group: EDLFilter[]) => {
    onSave(local.filter((f) => !group.includes(f)))
    setOpen(undefined)
  }

  const onApply = () => {
    setOpen(undefined)
    onSave(local)
  }

  const onTag = (tag: TagGroup) => {
    // reset local to items
    setLocal(items)

    if (open === tag) {
      setOpen(undefined)
    } else {
      setIsFilterOpen()
      setOpen(tag)
    }
  }

  const onItemSelected = (item: EDLFilter) => {
    const selected = !!local.find((f) => f === item)

    if (selected) {
      setLocal(local.filter((f) => f !== item))
    } else {
      setLocal([...local, item])
    }
  }

  const categoryName = !!items.find((i) => CATEGORIES.includes(i))
    ? items
        .filter((i) => CATEGORIES.includes(i))
        .map((item) => i18n.t(`screens.edlList.filters.${item}`))
        .join(', ')
    : i18n.t('screens.edlList.tags.category')

  const typeName = !!items.find((i) => TYPES.includes(i))
    ? items
        .filter((i) => TYPES.includes(i))
        .map((item) => i18n.t(`screens.edlList.filters.${item}`))
        .join(', ')
    : i18n.t('screens.edlList.tags.type')

  const renderItem = (item: EDLFilter) => {
    const selected = local.includes(item)

    return (
      <CheckboxContainer key={item}>
        <Checkbox
          label={i18n.t(`screens.edlList.filters.${item}`)}
          onClick={() => onItemSelected(item)}
          value={selected}
        />
      </CheckboxContainer>
    )
  }

  const renderGroup = (group: EDLFilter[]) => {
    const selected = !!local.find((i) => group.includes(i))

    return (
      <FilterOverView>
        <FilterView>{group.map(renderItem)}</FilterView>
        <Footer>
          {selected ? (
            <Erase onPress={() => reset(group)}>{i18n.t('screens.incidentList.filters.modalWeb.erase')}</Erase>
          ) : (
            <View />
          )}
          <Button label={i18n.t('screens.incidentList.filters.modalWeb.save')} onClick={onApply} status="active" />
        </Footer>
      </FilterOverView>
    )
  }

  const renderTag = (tag: TagGroup) => {
    const selected =
      tag === 'category' ? !!CATEGORIES.find((c) => items.includes(c)) : !!TYPES.find((c) => items.includes(c))

    return (
      <Hoverable onHover={onHover} key={tag}>
        <>
          <TagContainer>
            <Tag
              item={{ label: tag === 'category' ? categoryName : typeName, value: tag }}
              onItemSelected={() => onTag(tag)}
              selected={open === tag || selected}
              inactiveColor
            />
          </TagContainer>
          {open === tag && renderGroup(tag === 'category' ? CATEGORIES : TYPES)}
        </>
      </Hoverable>
    )
  }

  return (
    <Container>
      {TAGS.map(renderTag)}
      <ResetText onPress={onReset}>{i18n.t('screens.incidentList.filters.reset')}</ResetText>
    </Container>
  )
}

export default InlineFilters

const Container = styled(View)`
  flex-flow: row wrap;
  align-items: center;
`

const TagContainer = styled(View)`
  margin-right: 8px;
  margin-bottom: 8px;
  margin-top: 8px;
`

const ResetText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: 16px;
  text-decoration: underline;
  margin: 16px 0;
`

const FilterOverView = styled(View)`
  position: absolute;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 8px;
  top: 60px;
  min-width: 350px;
  ${(props) => props.theme.constants.shadow};
`

const FilterView = styled(View)`
  padding: 31px 22px 31px 31px;
`

const CheckboxContainer = styled(View)`
  margin-bottom: 15px;
`

const Footer = styled(View)`
  border-top-width: 1px;
  border-top-color: ${(props) => props.theme.colors.abandoned};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 16px 31px;
`

const Erase = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`
